/**
 * Normalisation E.164 côté navigateur — miroir de `crm.telephony_policy.to_e164`.
 *
 * Avec Twilio, le numéro composé transitait par le serveur, qui le normalisait
 * avant de le remettre à l'opérateur. Avec le softphone Telnyx, le navigateur
 * compose directement : un « 01 23 45 67 89 » lu sur une fiche partait tel
 * quel et se faisait refuser. Les règles sont celles du backend, à l'identique,
 * pour que le même numéro parte sous la même forme quel que soit le chemin.
 */
/** Premier numéro d'un champ (« 04… / 06… »), sans « (0) » ni extension en lettres. */
function premierNumero(s) {
  let t = s.split(/\s*(?:\/|;|,|\||\bou\b|\bet\b)\s*/)[0];
  t = t.replace(/\(\s*0\s*\)/g, "");
  return t.split(/[A-Za-z\u00C0-\u00FF]/)[0];
}

export function toE164(raw) {
  const s = premierNumero(String(raw || "").trim());
  const digits = s.replace(/[^\d+]/g, "");
  let out;
  if (digits.startsWith("+")) out = "+" + digits.slice(1).replace(/\D/g, "");
  else {
    const d = digits.replace(/\D/g, "");
    if (d.startsWith("0033")) out = "+33" + d.slice(4);
    else if (d.startsWith("00") && d.length >= 10) out = "+" + d.slice(2);
    else if (d.startsWith("33") && d.length >= 11) out = "+" + d;
    // Un numéro national (0…) est français, quelle que soit sa longueur : un
    // 0 en tête n'existe pas en E.164, et « +047812345 » cacherait l'erreur.
    else if (d.startsWith("0")) out = "+33" + d.slice(1);
    else if (d) out = "+" + d;
    else return "";
  }
  // « +33 06… » : le 0 national ne se compose pas derrière l'indicatif.
  if (out.startsWith("+330") && out.length === 13) out = "+33" + out.slice(4);
  return out;
}

/** Numéro français composable : +33 puis 9 chiffres, le premier différent de 0. */
const FR_E164 = /^\+33[1-9]\d{8}$/;
const E164 = /^\+[1-9]\d{7,14}$/;

/**
 * Ce qui empêche de composer, en une phrase pour l'agent — ou null si le
 * numéro est bon. Vérifié AVANT de solliciter l'opérateur : un numéro à
 * 9 chiffres ou un « +33 0… » finissait chez Twilio en erreur de passerelle
 * (31005/13224), c'est-à-dire un raccroché sans explication.
 */
export function dialProblem(raw) {
  const brut = String(raw || "").trim();
  if (!brut) return "Aucun numéro à composer.";
  const e164 = toE164(brut);
  const chiffres = e164.replace(/\D/g, "").length;
  if (!e164 || chiffres < 8) return `Numéro trop court (${chiffres} chiffre${chiffres > 1 ? "s" : ""}) : « ${brut} ».`;
  if (e164.startsWith("+33")) {
    const national = e164.length - 3;
    if (!FR_E164.test(e164)) {
      return `Numéro français mal formé : « ${brut} » donne ${national} chiffre${national > 1 ? "s" : ""} après l'indicatif, il en faut 9 (ex. 04 78 12 34 56).`;
    }
    return null;
  }
  if (!E164.test(e164)) return `Numéro invalide : « ${brut} » (format attendu : +indicatif puis 8 à 15 chiffres).`;
  return null;
}
